/**
 * CaseTrackUS - Privacy Policy Page
 * Updated: June 25, 2026
 * Author: Irina Panferova | Panferov LLC
 * Copyright: (c) 2026 Panferov LLC. All rights reserved.
 */

import React from 'react';
import { Container, Box, Typography, Button, Divider } from '@mui/material';

const P = ({ children, sx = {} }) => (
  <Typography variant="body1" sx={{ mb: 1.5, lineHeight: 1.8, color: '#2C2C2C', fontSize: '0.95rem', ...sx }}>
    {children}
  </Typography>
);

const Li = ({ children }) => (
  <Typography component="li" variant="body1" sx={{ mb: 0.75, lineHeight: 1.8, color: '#2C2C2C', fontSize: '0.95rem', ml: 2 }}>
    {children}
  </Typography>
);

const Section = ({ number, title, children }) => (
  <Box sx={{ mb: 5 }}>
    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 2, mb: 2, pb: 1, borderBottom: '2px solid #1565C0' }}>
      <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#1565C0', letterSpacing: 3, textTransform: 'uppercase', minWidth: 24 }}>
        {number}
      </Typography>
      <Typography variant="h6" sx={{ fontWeight: 700, color: '#1A1A1A', letterSpacing: 0.5, textTransform: 'uppercase', fontSize: '0.9rem' }}>
        {title}
      </Typography>
    </Box>
    {children}
  </Box>
);

const Sub = ({ title, children }) => (
  <Box sx={{ mb: 2.5, pl: 2, borderLeft: '3px solid #E3F2FD' }}>
    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1565C0', mb: 1, fontSize: '0.9rem' }}>
      {title}
    </Typography>
    {children}
  </Box>
);

const TableRow = ({ cells, header }) => (
  <Box sx={{
    display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr',
    borderBottom: '1px solid #E3F2FD',
    background: header ? '#1565C0' : 'transparent'
  }}>
    {cells.map((cell, i) => (
      <Box key={i} sx={{ p: 1.5, borderRight: i < cells.length - 1 ? `1px solid ${header ? 'rgba(255,255,255,0.2)' : '#E3F2FD'}` : 'none' }}>
        <Typography sx={{
          fontSize: '0.8rem',
          fontWeight: header ? 700 : 400,
          color: header ? 'white' : '#2C2C2C',
          letterSpacing: header ? 1 : 0,
          lineHeight: 1.5
        }}>
          {cell}
        </Typography>
      </Box>
    ))}
  </Box>
);

export default function Privacy() {
  return (
    <Box sx={{ minHeight: '100vh', background: '#FAFAFA' }}>

      {/* Header */}
      <Box sx={{ background: '#1A1A1A', color: 'white', py: 2.5 }}>
        <Container maxWidth="md">
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography sx={{ fontWeight: 800, fontSize: '1.1rem', letterSpacing: 1 }}>🗽 CASETRACKUS</Typography>
              <Typography sx={{ fontSize: '0.7rem', color: '#aaa', letterSpacing: 2, textTransform: 'uppercase' }}>by Panferov LLC</Typography>
            </Box>
            <Button href="/" variant="outlined" size="small"
              sx={{ color: 'white', borderColor: 'rgba(255,255,255,0.4)', fontSize: '0.75rem', letterSpacing: 1 }}>
              ← Back to App
            </Button>
          </Box>
        </Container>
      </Box>

      {/* Title block */}
      <Box sx={{ background: '#0D47A1', color: 'white', py: 5 }}>
        <Container maxWidth="md">
          <Typography sx={{ fontSize: '0.7rem', letterSpacing: 4, textTransform: 'uppercase', color: 'rgba(255,255,255,0.6)', mb: 1 }}>
            Legal Document
          </Typography>
          <Typography variant="h3" sx={{ fontWeight: 800, mb: 1.5, letterSpacing: -0.5 }}>
            Privacy Policy
          </Typography>
          <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
            <Typography sx={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.7)' }}>Effective: March 23, 2026</Typography>
            <Typography sx={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.7)' }}>Last Updated: June 25, 2026</Typography>
            <Typography sx={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.7)' }}>Panferov LLC · Illinois, USA</Typography>
          </Box>
        </Container>
      </Box>

      <Container maxWidth="md" sx={{ py: 6 }}>

        {/* Intro */}
        <P>CaseTrackUS is operated by Panferov LLC ("we", "us", "our"), a limited liability company organized under the laws of the State of Illinois, USA. This Privacy Policy describes how we collect, use, store, share, and protect personal data when you use the CaseTrackUS website at https://casetrackus.com and related services including our browser extension (collectively, the "Service").</P>
        <P>We understand that immigration data is among the most sensitive personal information. We are committed to handling it with the utmost care, transparency, and security.</P>
        <P sx={{ mb: 4 }}>Questions: <strong>privacy@casetrackus.com</strong></P>

        <Section number="01" title="Changes to This Policy">
          <P>We will notify you of material changes by email at least 14 days before changes take effect. Material change notifications will include a plain-language summary of what has changed. We will obtain your active consent for any changes that materially affect how we use your personal data. Continued use after changes take effect constitutes acceptance. The date of last revision is indicated at the beginning of this document.</P>
        </Section>

        <Section number="02" title="Data Controller">
          <Box sx={{ background: '#F8F9FA', border: '1px solid #E3F2FD', borderRadius: 1, p: 2.5 }}>
            <P sx={{ fontWeight: 700, mb: 0.5 }}>Panferov LLC is the data controller responsible for your personal data.</P>
            <P sx={{ mb: 0.5 }}>Privacy questions: <strong>privacy@casetrackus.com</strong></P>
            <P sx={{ mb: 0.5 }}>Legal matters: <strong>legal@casetrackus.com</strong></P>
            <P sx={{ mb: 0 }}>Address: Illinois, USA</P>
          </Box>
        </Section>

        <Section number="03" title="What Information We Collect">
          <Sub title="3.1 Information You Provide Directly">
            <ul>
              <Li>Name and email address (required for account creation)</Li>
              <Li>Phone number (optional — for SMS notifications when available)</Li>
              <Li>USCIS receipt numbers and immigration case identifiers</Li>
              <Li>EOIR case data: name, A-Number, and nationality as recorded in the court system</Li>
              <Li>DOL FLAG case numbers</Li>
              <Li>For professional users: client data and family members' data</Li>
              <Li>Password (stored encrypted using bcrypt; plain text never stored)</Li>
              <Li>Google account data (name, email, photo) if using Google sign-in</Li>
              <Li>Payment billing information (card data processed by Stripe, never stored by us)</Li>
              <Li>Support correspondence and feedback</Li>
            </ul>
          </Sub>

          <Sub title="3.2 Information Retrieved from Government Sources">
            <P>When you add a case, we retrieve publicly available information using your identification data. Government sources determine the volume and composition of data returned:</P>
            <ul>
              <Li><strong>USCIS Case Status API</strong> — case status, form type, status description, processing metadata</Li>
              <Li><strong>USCIS FOIA API</strong> — request status only. Documents are delivered to your USCIS account, not stored by CaseTrackUS</Li>
              <Li><strong>EOIR ACIS</strong> — hearing dates, court decisions, BIA appeal status, judge name, court address, hearing type</Li>
              <Li><strong>DOL FLAG</strong> — labor condition application status and employer data</Li>
            </ul>
            <Box sx={{ background: '#FFF8E1', border: '1px solid #F9A825', borderRadius: 1, p: 2, mt: 1.5 }}>
              <P sx={{ mb: 0, fontSize: '0.85rem' }}>⚠️ <strong>Important:</strong> government systems may contain incorrect data or experience update delays. CaseTrackUS is not responsible for the accuracy of government source data.</P>
            </Box>
          </Sub>

          <Sub title="3.3 Information Collected by the Browser Extension">
            <P>The CaseTrackUS EOIR Checker Chrome extension collects:</P>
            <ul>
              <Li>EOIR case information retrieved from acis.eoir.justice.gov using your case identifiers</Li>
              <Li>Authentication token stored locally on your device to communicate with CaseTrackUS servers</Li>
            </ul>
            <P>The extension:</P>
            <ul>
              <Li>Operates only on the device where installed, under the Account holder's direct control</Li>
              <Li>Accesses ONLY the EOIR ACIS website — does not access any other websites you visit</Li>
              <Li>Does not collect browsing history, bookmarks, passwords, or any other device data</Li>
              <Li>Transmits retrieved case data exclusively to CaseTrackUS servers</Li>
            </ul>
          </Sub>

          <Sub title="3.4 Advertising-Related Information">
            <P>On the free Individual plan, third-party advertising networks may collect: cookie identifiers, approximate location (city/region), and general page visit data within the Service.</P>
            <Box sx={{ background: '#E8F5E9', border: '1px solid #A5D6A7', borderRadius: 1, p: 2, mt: 1 }}>
              <P sx={{ mb: 0, fontSize: '0.85rem', fontWeight: 600 }}>We strictly do NOT share with advertisers: A-Numbers, receipt numbers, immigration status, case outcomes, hearing dates, court decisions, nationality (for advertising), or any case-specific information.</P>
            </Box>
          </Sub>

          <Sub title="3.5 Automatically Collected Technical Data">
            <ul>
              <Li>IP address and approximate location (country/region level)</Li>
              <Li>Browser type, operating system, device type</Li>
              <Li>Pages visited and features used within the Service</Li>
              <Li>Login timestamps and session logs</Li>
            </ul>
            <P>Used for security, fraud prevention, and Service improvement only. Not used for advertising profiling.</P>
          </Sub>
        </Section>

        <Section number="04" title="How We Use Your Information">
          <ul>
            <Li><strong>Providing the Service:</strong> creating and managing your Account, retrieving case status, displaying case information</Li>
            <Li><strong>Notifications:</strong> sending email notifications when case status changes</Li>
            <Li><strong>Automatic monitoring:</strong> scheduled USCIS checks (twice weekly), EOIR checks (weekly via extension)</Li>
            <Li><strong>Payment processing:</strong> billing through Stripe based on your plan</Li>
            <Li><strong>Customer support:</strong> responding to requests in written English</Li>
            <Li><strong>Security and fraud prevention:</strong> detecting and preventing unauthorized access</Li>
            <Li><strong>Service improvement:</strong> analyzing usage patterns and improving features</Li>
            <Li><strong>Legal compliance:</strong> fulfilling applicable legal obligations</Li>
            <Li><strong>Advertising:</strong> displaying third-party ads on the free Individual plan only</Li>
          </ul>
          <P><strong>We do NOT</strong> use immigration case data for advertising targeting, profiling, data sales, or any purpose unrelated to providing the Service.</P>
        </Section>

        <Section number="05" title="Legal Bases for Processing (GDPR)">
          <ul>
            <Li><strong>Contract performance</strong> (Art. 6(1)(b)): processing necessary to provide the Service</Li>
            <Li><strong>Legitimate interests</strong> (Art. 6(1)(f)): security, fraud prevention, Service improvement</Li>
            <Li><strong>Legal obligation</strong> (Art. 6(1)(c)): compliance with applicable legal obligations</Li>
            <Li><strong>Consent</strong> (Art. 6(1)(a)): SMS notifications and advertising where consent obtained</Li>
          </ul>
        </Section>

        <Section number="06" title="How We Share Your Information">
          <P>We do not sell your personal data. We share information only in the following limited cases:</P>

          <Sub title="6.1 Third-Party Service Providers">
            <Box sx={{ border: '1px solid #BBDEFB', borderRadius: 1, overflow: 'hidden', mb: 2 }}>
              <TableRow header cells={['PROVIDER', 'PURPOSE', 'DATA SHARED', 'POLICY']} />
              <TableRow cells={['Hetzner (Germany)', 'Cloud hosting', 'All account data (encrypted)', 'hetzner.com/legal']} />
              <TableRow cells={['Resend', 'Email notifications', 'Email, notification content', 'resend.com/privacy']} />
              <TableRow cells={['Stripe', 'Payment processing', 'Billing info (card → Stripe directly)', 'stripe.com/privacy']} />
              <TableRow cells={['Google (OAuth)', 'Sign-in', 'Name, email (if Google login used)', 'policies.google.com']} />
              <TableRow cells={['Ad Networks', 'Ads (free plan only)', 'Anonymous IDs, general usage — NO case data', 'Per network']} />
              <TableRow cells={['USCIS API', 'Case status', 'Receipt number only', 'uscis.gov/privacy']} />
              <TableRow cells={['EOIR ACIS', 'Court data (extension)', 'Name, A-Number, nationality', 'justice.gov/eoir']} />
              <TableRow cells={['DOL FLAG', 'Labor case data', 'Case number only', 'dol.gov/privacy']} />
            </Box>
          </Sub>

          <Sub title="6.2 Legal Requirements">
            <P>We may disclose data if required by valid legal process. See Section 7 for special immigration data protections.</P>
          </Sub>

          <Sub title="6.3 Corporate Transactions">
            <P>In case of merger or acquisition, we will notify you before your data becomes subject to a different privacy policy and provide the opportunity to delete your Account. If CaseTrackUS ceases operations, users will be notified at least 30 days in advance and provided with options to securely export or permanently delete their data before the Service is discontinued.</P>
          </Sub>
          <Sub title="6.4 Third-Party Data Use Restrictions">
            <P>All third-party service providers are contractually prohibited from using, disclosing, selling, or sharing user data (including de-identified, anonymized, or pseudonymized data) for any purpose other than providing services to CaseTrackUS. Third parties are bound by data processing agreements that align with the terms and conditions of this Privacy Policy. We do not permit any third party to use your data for their own marketing, advertising, or commercial purposes. User data is never sold or licensed to any third party for profit or other monetary transactions.</P>
          </Sub>
        </Section>

        <Section number="07" title="Special Protection of Immigration Data">
          <Box sx={{ background: '#E8F5E9', border: '1px solid #A5D6A7', borderRadius: 1, p: 3, mb: 2 }}>
            <P sx={{ fontWeight: 700, mb: 1.5, color: '#1B5E20' }}>We apply the following additional protections to immigration data:</P>
            <ul>
              <Li>We do <strong>NOT</strong> share immigration case data with law enforcement, ICE, CBP, or any government enforcement agency — except when strictly required by a valid court order</Li>
              <Li>We do <strong>NOT</strong> use immigration data to infer or disclose immigration status, nationality, or citizenship to third parties</Li>
              <Li>We do <strong>NOT</strong> sell or license immigration case data</Li>
              <Li>We do <strong>NOT</strong> share A-Numbers, receipt numbers, case outcomes, or hearing information with advertising networks under any circumstances</Li>
              <Li>Access to immigration case data is limited to authorized personnel on a need-to-know basis</Li>
            </ul>
          </Box>
        </Section>

        <Section number="08" title="Advertising and Data Use">
          <Sub title="8.1 How Advertising Works">
            <P>The free Individual plan is supported by third-party advertisements. All paid plans (Solo Attorney and above) are completely ad-free.</P>
          </Sub>
          <Sub title="8.2 What We Never Share with Advertisers">
            <P>Regardless of plan or any other factor, we never share with advertising networks:</P>
            <ul>
              <Li>A-Numbers or USCIS receipt numbers</Li>
              <Li>Immigration status or case outcomes</Li>
              <Li>Nationality or country of origin</Li>
              <Li>Hearing dates, court decisions, or any case-specific information</Li>
              <Li>Name or email address for advertising purposes</Li>
            </ul>
          </Sub>
          <Sub title="8.3 Attorney Advertisements">
            <P>Advertisements from immigration attorneys may be displayed. CaseTrackUS does not endorse any advertised attorney and is not responsible for the quality of their services. Nothing in an advertisement constitutes legal advice.</P>
          </Sub>
          <Sub title="8.4 Advertising Opt-Out">
            <P>You may opt out through your browser's cookie settings. Opting out means ads will not be personalized, but you may still see non-targeted ads on the free plan. Upgrading to any paid plan removes all advertising.</P>
          </Sub>
        </Section>

        <Section number="09" title="Professional Users' Responsibility for Client Data">
          <P>Professional users adding client data bear independent responsibility for compliance with applicable data protection law (GDPR, CCPA, and other applicable privacy laws) regarding their clients' personal data. CaseTrackUS processes client data exclusively to provide the Service. Professional users must ensure they have appropriate legal bases for processing client data through the Service. CaseTrackUS is not liable for data protection violations committed by professional users.</P>
        </Section>

        <Section number="10" title="Data Security">
          <ul>
            <Li>All data transmitted using TLS/HTTPS encryption</Li>
            <Li>Passwords hashed using bcrypt with salt — we cannot recover your original password</Li>
            <Li>JWT tokens with defined expiration for session management</Li>
            <Li>Database encrypted at rest on Hetzner infrastructure (Germany, EU)</Li>
            <Li>Access to production systems limited to authorized personnel</Li>
            <Li>Regular security monitoring and updates</Li>
          </ul>
          <P>In the event of a data breach, we will notify affected users and relevant authorities within required timeframes (72 hours under GDPR where applicable).</P>
        </Section>

        <Section number="11" title="Data Retention">
          <Box sx={{ border: '1px solid #BBDEFB', borderRadius: 1, overflow: 'hidden', mb: 2 }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', background: '#0D47A1', color: 'white' }}>
              <Box sx={{ p: 1.5 }}><Typography sx={{ fontWeight: 700, fontSize: '0.8rem', letterSpacing: 1 }}>DATA TYPE</Typography></Box>
              <Box sx={{ p: 1.5, borderLeft: '1px solid rgba(255,255,255,0.2)' }}><Typography sx={{ fontWeight: 700, fontSize: '0.8rem', letterSpacing: 1 }}>RETENTION PERIOD</Typography></Box>
            </Box>
            {[
              ['Account data, cases, profiles, history', 'Deleted immediately upon Account deletion request'],
              ['Payment records', 'Up to 7 years (tax/financial law)'],
              ['Security and access logs', 'Up to 12 months'],
              ['Support correspondence', 'Up to 3 years'],
              ['Anonymized analytics data', 'May be retained indefinitely (not linked to any individual)'],
            ].map(([type, period], i) => (
              <Box key={i} sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', borderBottom: '1px solid #E3F2FD', background: i % 2 === 0 ? '#F8F9FA' : 'white' }}>
                <Box sx={{ p: 1.5, borderRight: '1px solid #E3F2FD' }}><Typography sx={{ fontSize: '0.85rem', color: '#2C2C2C' }}>{type}</Typography></Box>
                <Box sx={{ p: 1.5 }}><Typography sx={{ fontSize: '0.85rem', color: '#2C2C2C' }}>{period}</Typography></Box>
              </Box>
            ))}
          </Box>
          <Box sx={{ background: '#E8F5E9', border: '1px solid #A5D6A7', borderRadius: 1, p: 2 }}>
            <P sx={{ mb: 0, fontWeight: 600, fontSize: '0.9rem' }}>Account Deletion: Upon your request through Account Settings, we immediately and permanently delete your personal data, cases, and history. This action is irreversible. We retain only payment records (required by tax law) and anonymized data that cannot be linked to you.</P>
          </Box>
        </Section>

        <Section number="12" title="Cookies and Tracking Technologies">
          <ul>
            <Li><strong>Session cookies:</strong> maintain your login session; deleted when browser closes</Li>
            <Li><strong>Security cookies:</strong> detect and prevent fraud and unauthorized access</Li>
            <Li><strong>Preference cookies:</strong> remember display settings</Li>
            <Li><strong>Advertising cookies</strong> (free Individual plan only): served by third-party advertising networks</Li>
          </ul>
          <P>We do NOT use advertising cookies on paid plans. We do not use Google Analytics or cross-site tracking tools. You can manage cookies through your browser settings.</P>
        </Section>

        <Section number="13" title="Your Rights and Choices">
          <ul>
            <Li><strong>Right of access:</strong> request a copy of your personal data</Li>
            <Li><strong>Right to rectification:</strong> request correction of inaccurate data</Li>
            <Li><strong>Right to erasure:</strong> available directly through Account Settings → Delete Account</Li>
            <Li><strong>Right to data portability:</strong> request data in machine-readable format</Li>
            <Li><strong>Right to restriction:</strong> request restriction of processing in certain circumstances</Li>
            <Li><strong>Right to object:</strong> object to processing based on legitimate interests</Li>
            <Li><strong>Right to withdraw consent:</strong> withdraw consent at any time</Li>
            <Li><strong>Right against automated decisions:</strong> we do not make automated decisions with legal effects on you</Li>
          </ul>
          <P>To exercise rights: <strong>privacy@casetrackus.com</strong>. Response within 30 days. We may verify your identity before processing your request.</P>
        </Section>

        <Section number="14" title="International Data Transfers">
          <P>Our servers are hosted by Hetzner in Germany (European Union), benefiting from EU-level data protection. For data transfers from the EEA, UK, or Switzerland to any non-EU processors, we rely on Standard Contractual Clauses (SCCs) or other lawful transfer mechanisms.</P>
        </Section>

        <Section number="15" title="Children's Privacy">
          <P>Account creation requires age 18+. Immigration cases of minors may be tracked through parent, guardian, or professional user accounts. We do not knowingly collect data directly from children under 18. Contact privacy@casetrackus.com if you believe we have such data.</P>
        </Section>

        <Section number="16" title="California Residents (CCPA/CPRA)">
          <P><strong>Categories collected:</strong> Identifiers (name, email, IP); Personal information; Commercial information (subscriptions); Internet activity (Service page views); Geolocation (country/region only).</P>
          <P><strong>We do NOT</strong> sell personal data or share it for cross-context behavioral advertising.</P>
          <P><strong>Your rights:</strong> access, delete (via Account Settings), correct, opt-out of sale/sharing (not applicable — we don't sell), non-discrimination.</P>
          <P>Contact: <strong>privacy@casetrackus.com</strong> — subject line "California Privacy Request". Response within 45 days.</P>
        </Section>

        <Section number="17" title="Additional US State Residents">
          <P><strong>Colorado (CPA), Virginia (VCDPA), Connecticut (CDPA), Utah (UCPA), Nevada:</strong> We do not sell personal data. You have rights of access, correction, deletion, and portability. Contact privacy@casetrackus.com. Response within 45 days.</P>
        </Section>

        <Section number="18" title="EEA, UK, and Switzerland Residents (GDPR)">
          <P>You have all GDPR rights (Arts. 15–22). You may lodge complaints with your national supervisory authority (UK: ICO; Switzerland: FDPIC). Our servers are in Germany (EU). Contact privacy@casetrackus.com. Response within 30 days.</P>
        </Section>

        <Section number="19" title="Brazil (LGPD) and Canada (PIPEDA)">
          <P><strong>Brazil:</strong> You have all LGPD rights. Response within 15 days. If unsatisfied, contact ANPD.</P>
          <P><strong>Canada:</strong> We comply with PIPEDA and applicable provincial privacy legislation including Quebec Law 25. Contact privacy@casetrackus.com to exercise rights.</P>
        </Section>

        <Section number="20" title="Do Not Track Signals">
          <P>Our Service does not currently respond to Do Not Track (DNT) browser signals. We do not engage in cross-site tracking of users.</P>
        </Section>

        <Section number="21" title="Contact Us">
          <P>For privacy questions, to exercise your rights, or to submit a data subject request — contact us exclusively in written form in English:</P>
          <Box sx={{ background: '#F8F9FA', border: '1px solid #BBDEFB', borderRadius: 1, p: 3 }}>
            <P sx={{ fontWeight: 700, mb: 0.5 }}>CaseTrackUS, operated by Panferov LLC</P>
            <P sx={{ mb: 0.5 }}>Privacy & data rights: <strong>privacy@casetrackus.com</strong></P>
            <P sx={{ mb: 0.5 }}>Legal matters: <strong>legal@casetrackus.com</strong></P>
            <P sx={{ mb: 0.5 }}>General support: <strong>support@casetrackus.com</strong></P>
            <P sx={{ mb: 0.5 }}>Website: <strong>https://casetrackus.com/privacy</strong></P>
            <P sx={{ mb: 0 }}>Address: Illinois, USA</P>
          </Box>
          <P sx={{ mt: 2, color: '#666', fontSize: '0.85rem' }}>Response within applicable legal deadlines. Security matters: 72 hours.</P>
        </Section>

        <Divider sx={{ my: 4 }} />
        <Box sx={{ textAlign: 'center', pb: 4, display: 'flex', gap: 2, justifyContent: 'center', mb: 3 }}>
          <Button href="/" variant="contained" sx={{ background: '#0D47A1', px: 3 }}>← Back to CaseTrackUS</Button>
          <Button href="/terms" variant="outlined" sx={{ color: '#0D47A1', borderColor: '#0D47A1', px: 3 }}>Terms of Service →</Button>
        </Box>
        <Typography sx={{ textAlign: 'center', color: '#999', fontSize: '0.8rem', letterSpacing: 1 }}>
          © 2026 Panferov LLC. All rights reserved. CaseTrackUS is a trade name of Panferov LLC.
        </Typography>

      </Container>
    </Box>
  );
}
